import clsx from 'clsx'

import { Redact } from '@/common/analytics'
import { Badge } from '@/common/components/badge'
import { Text } from '@/common/components/text'
import type { ClosingTimeline, PropertyResponse } from '../../api/properties'
import * as styles from './PropertyKeyDetailsCard.css'

interface PropertyKeyDetailsCardProps {
  property: PropertyResponse
  className?: string
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatClosingTimeline(timeline: ClosingTimeline): string {
  switch (timeline) {
    case '14_days':
      return '14 day close'
    case '30_days':
      return '30 day close'
    case '30_60_days':
      return '30–60 day close'
    case 'flexible':
      return 'Flexible close'
    default:
      return timeline
  }
}

interface KeyDetailRowProps {
  label: string
  value: string
  /** Marks a value the seller supplied, as opposed to a third-party estimate. */
  sellerStated?: boolean
  /** Wraps the value for Heap DOM redaction (dollar amounts only). */
  redactValue?: boolean
}

function KeyDetailRow({ label, value, sellerStated, redactValue }: KeyDetailRowProps) {
  const valueText = (
    <Text as="span" variant="body" weight="bold">
      {value}
    </Text>
  )

  return (
    <div className={styles.row}>
      <Text as="span" variant="bodySm" color="muted" className={styles.rowLabel}>
        {label}
      </Text>
      <div className={styles.rowValue}>
        {redactValue ? <Redact as="span">{valueText}</Redact> : valueText}
        {sellerStated && (
          <Badge color="neutral" size="sm">
            Seller-stated
          </Badge>
        )}
      </div>
    </div>
  )
}

/**
 * Reference details shown alongside the offer form: the AVM estimate plus the
 * seller's floor price and closing timeline.
 *
 * `floor_price` is gated server-side by `share_floor_price`, so its absence here
 * means the seller isn't sharing it — the row is simply omitted. Renders nothing
 * when the property has none of the three values.
 */
export function PropertyKeyDetailsCard({ property, className }: PropertyKeyDetailsCardProps) {
  const { house_canary_valuation, floor_price, closing_timeline } = property

  if (house_canary_valuation == null && floor_price == null && closing_timeline == null) {
    return null
  }

  return (
    <div className={clsx(styles.card, className)}>
      <Text as="span" variant="label" color="muted" className={styles.title}>
        Key details
      </Text>

      {house_canary_valuation != null && (
        <KeyDetailRow label="AVM" value={formatCurrency(house_canary_valuation)} redactValue />
      )}
      {floor_price != null && (
        <KeyDetailRow
          label="Floor price"
          value={formatCurrency(floor_price)}
          sellerStated
          redactValue
        />
      )}
      {closing_timeline != null && (
        <KeyDetailRow
          label="Closing timeline"
          value={formatClosingTimeline(closing_timeline)}
          sellerStated
        />
      )}
    </div>
  )
}
